import { RouteComponentProps } from 'react-router-dom';
import { includes } from 'es-toolkit/compat';
import queryString, { parse, stringify } from '@tock/query-string';

import { Button, Typography } from 'omakase';
import Anchor from 'components/Anchor';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ButtonGroup from '@tock/shared/components/buttons/ButtonGroup';
import connect, { DefaultConnectProps } from '@tock/shared/utils/connect';

export interface SignedOutRouteProps {
  title: string;
  description: string;
  buttonText: string;
  noAccountActionText: string;
  noAccountActionLocation: {
    pathname: string;
    search?: string;
  };
  icon: string;
}

export const reservationSignedOutProps: SignedOutRouteProps = {
  title: 'Reservations',
  description: 'Your place to keep, manage, and prepare for your reservations.',
  buttonText: 'Log in to view reservations',
  noAccountActionText: 'Explore!',
  noAccountActionLocation: {
    pathname: '/',
  },
  icon: 'icon_calendar',
};

export const profileSignedOutProps: SignedOutRouteProps = {
  title: 'Profile',
  description: 'A personalized diner profile, giving you access to exclusive culinary experiences.',
  buttonText: 'Log in to view profile',
  noAccountActionText: 'Create one',
  noAccountActionLocation: {
    pathname: '/signup',
  },
  icon: 'icon_person-unfilled',
};

class SignedOutRoute extends BaseComponent<
  SignedOutRouteProps & DefaultConnectProps & RouteComponentProps
> {
  getContinuePath() {
    return queryString.parse(location.search).continue;
  }

  componentDidMount() {
    const { app, history, title } = this.props;
    this.props.actions.navigation.set({
      title,
    });
    const isAuthenticated = !!app.jwtToken;
    if (isAuthenticated) {
      history.replace(this.getContinuePath());
    }
  }

  handleClick() {
    this.props.history.push({
      pathname: '/login',
      search: stringify({
        continue: location.pathname === '/logout' ? '/' : this.getContinuePath(),
      }),
    });
  }

  handleNoAccountActionClick() {
    const { pathname } = this.props.noAccountActionLocation;
    let { search } = this.props.noAccountActionLocation;
    if (includes(this.props.noAccountActionLocation.pathname, 'login')) {
      search = stringify({
        ...parse(search || ''),
        continue: location.pathname === '/logout' ? '/' : this.getContinuePath(),
      });
    }
    this.props.history.push({ pathname, search });
  }

  render() {
    return (
      <div key={this.props.noAccountActionLocation.pathname} className="SignedOut-body">
        <div className="Signout-header">
          <Typography color="inherit" component="span" variant="heading03">
            {this.props.title}
          </Typography>
        </div>
        <div className="SignedOut-container">
          <div className="SignedOut-contentContainer">
            <div className="SignedOut-topWrapper">
              <div className="SignedOut-icon">
                <div className="SignedOut-circle">
                  <i className={`tock-icon ${this.props.icon} SignedOut-iconTop`} />
                </div>
              </div>
              <div className="SignedOut-description">
                <Typography color="inherit" component="span" variant="body02Strong">
                  {this.props.description}
                </Typography>
              </div>
            </div>

            <div className="SignedOut-bottomWrapper">
              <div className="SignedOut-login">
                <ButtonGroup>
                  <Button variant="primary" size="medium" fullWidth onClick={this.handleClick}>
                    {this.props.buttonText}
                  </Button>
                </ButtonGroup>
              </div>

              <div className="SignedOut-signup">
                <Typography color="inherit" component="span" variant="body02">
                  Don't have a Tock account?&nbsp;
                  <Anchor className="SignedOut-link" onClick={this.handleNoAccountActionClick}>
                    {this.props.noAccountActionText}
                  </Anchor>
                </Typography>
              </div>

              <div className="SignedOut-terms">
                <Typography color="inherit" component="span" variant="body02">
                  <Anchor className="SignedOut-link" href="/terms">
                    Tock Terms of Use
                  </Anchor>
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect<RouteComponentProps & SignedOutRouteProps>(SignedOutRoute);
